import { useState, useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Check, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { AccountSecurityTab } from '@/components/settings/AccountSecurityTab'
import { NotificationPreferences } from '@/components/settings/NotificationPreferences'
import { UsersManagementTab } from '@/components/settings/UsersManagementTab'
import { ExportTemplatesTab } from '@/components/settings/ExportTemplatesTab'
import { IntegrationsTab } from '@/components/settings/IntegrationsTab'
import { DataPrivacyTab } from '@/components/settings/DataPrivacyTab'
import { GoNoGoTemplateSettings } from '@/components/settings/GoNoGoTemplateSettings'
import { WinThemeBankSettings } from '@/components/settings/WinThemeBankSettings'
import { useAuthStore } from '@/stores/auth-store'
import { useSettingsStore } from '@/stores/settings-store'
import { knowledgeTabPath } from '@/components/knowledge/knowledge-tabs'
import type { WritingPreferences } from '@/lib/schemas'

function BidStrategySubTabs() {
  const [subTab, setSubTab] = useState<'go-no-go' | 'win-themes'>('go-no-go')

  return (
    <Tabs value={subTab} onValueChange={(v) => setSubTab(v as typeof subTab)}>
      <TabsList className="bg-muted h-9 p-1 rounded-lg">
        <TabsTrigger value="go-no-go" className="rounded-md text-xs px-3 h-7 data-[state=active]:bg-card data-[state=active]:shadow-sm">
          Go / No-Go Template
        </TabsTrigger>
        <TabsTrigger value="win-themes" className="rounded-md text-xs px-3 h-7 data-[state=active]:bg-card data-[state=active]:shadow-sm">
          Win Themes
        </TabsTrigger>
      </TabsList>
      <TabsContent value="go-no-go" className="mt-6">
        <GoNoGoTemplateSettings />
      </TabsContent>
      <TabsContent value="win-themes" className="mt-6">
        <WinThemeBankSettings />
      </TabsContent>
    </Tabs>
  )
}

/**
 * Settings tabs with a canonical URL of their own: three that own a dedicated
 * route, plus Writing Preferences, the first tab, which is what bare
 * `/app/settings` shows. Everything else is addressed as `/app/settings?tab=<id>`.
 */
const TAB_URLS: Partial<Record<string, string>> = {
  preferences: '/app/settings',
  users: '/app/settings/users',
  'export-templates': '/app/settings/export-templates',
  integrations: '/app/settings/integrations',
}

/** Company Profile moved to Knowledge (R4); old `?tab=profile` links land there. */
const LEGACY_PROFILE_TAB = 'profile'
const COMPANY_PROFILE_PATH = knowledgeTabPath('company-profile')

const NAV_TRIGGER_CLASS =
  'justify-start h-8 px-3 rounded-lg data-[state=active]:bg-foreground/10 data-[state=active]:font-medium data-[state=active]:shadow-none'

export function SettingsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)

  const {
    organization,
    isLoading,
    isSaving,
    error,
    loadSettings,
    saveWritingPreferences,
    clearError,
  } = useSettingsStore()

  // Local state for forms
  const [preferencesForm, setPreferencesForm] = useState<Partial<WritingPreferences>>({})
  // Saved snapshot for dirty detection
  const [savedPreferencesForm, setSavedPreferencesForm] = useState<Partial<WritingPreferences>>({})
  // Brief "Saved" confirmation flash
  const [preferencesSaved, setPreferencesSaved] = useState(false)

  const isPreferencesDirty = JSON.stringify(preferencesForm) !== JSON.stringify(savedPreferencesForm)

  type SettingsTab = 'preferences' | 'users' | 'bid-strategy' | 'export-templates' | 'integrations' | 'data-privacy' | 'account' | 'notifications'
  const validTabs: SettingsTab[] = ['preferences', 'users', 'bid-strategy', 'export-templates', 'integrations', 'data-privacy', 'account', 'notifications']

  // Three tabs have dedicated routes; the rest are addressed by `?tab=`. The
  // URL is the single source of truth for which tab is open, so a refresh, a
  // bookmark, a link pasted into a support reply and browser Back all land
  // where the user was.
  const resolveTabFromLocation = (pathname: string, search: string): SettingsTab => {
    const tabParam = new URLSearchParams(search).get('tab')
    if (tabParam && validTabs.includes(tabParam as SettingsTab)) return tabParam as SettingsTab
    if (pathname.endsWith('/users')) return 'users'
    if (pathname.endsWith('/export-templates')) return 'export-templates'
    if (pathname.endsWith('/integrations')) return 'integrations'
    return 'preferences'
  }

  const [activeTab, setActiveTab] = useState<SettingsTab>(() =>
    resolveTabFromLocation(location.pathname, location.search),
  )
  const isLegacyProfileLink = new URLSearchParams(location.search).get('tab') === LEGACY_PROFILE_TAB

  // Load settings on mount
  useEffect(() => {
    if (isLegacyProfileLink) return
    void loadSettings()
  }, [loadSettings, isLegacyProfileLink])

  // Sync the form when organization data loads (from initial load or after save)
  useEffect(() => {
    if (organization) {
      const prefsState = {
        ...(organization.writingPreferences || {}),
        // Column-backed switch surfaced through the preferences form
        autoHarvestEnabled: organization.autoHarvestEnabled ?? true,
      }
      setPreferencesForm(prefsState)
      setSavedPreferencesForm(prefsState)
    }
  }, [organization])

  // Follow the URL wherever it goes — including Back/Forward, which change
  // only the search string when moving between `?tab=` siblings.
  useEffect(() => {
    setActiveTab(resolveTabFromLocation(location.pathname, location.search))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])

  const handleSavePreferences = async (e: React.FormEvent) => {
    e.preventDefault()
    const success = await saveWritingPreferences(preferencesForm)
    if (success) {
      setPreferencesSaved(true)
      setTimeout(() => setPreferencesSaved(false), 2000)
    }
  }

  if (isLegacyProfileLink) {
    return <Navigate replace to={COMPANY_PROFILE_PATH} />
  }

  if (isLoading && !organization) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-2">Loading settings...</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Error Banner */}
      {error && (
        <div className="px-6 py-2 bg-destructive/10 text-destructive text-sm border-b flex items-center justify-between">
          <span>{error}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={clearError}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          const tab = value as SettingsTab
          setActiveTab(tab)
          // Every tab writes itself to the URL: the three with dedicated routes
          // keep their path, the rest ride `?tab=`. Pushed (not replaced) so
          // Back returns to the previous tab instead of leaving Settings.
          const target = TAB_URLS[tab] ?? `/app/settings?tab=${tab}`
          if (`${location.pathname}${location.search}` !== target) {
            void navigate(target)
          }
        }}
        orientation="vertical"
        className="flex flex-1 overflow-hidden"
      >
        {/* Side navigation — Workspace (org-level) then My account (personal) */}
        <div className="w-56 shrink-0 pl-6 pr-3 pt-[18px] space-y-1">
          <TabsList className="flex flex-col items-stretch h-auto bg-transparent gap-2">
            <div className="px-3 pb-1 text-xs font-medium text-foreground uppercase tracking-wide">Workspace</div>
            <TabsTrigger value="preferences" className={NAV_TRIGGER_CLASS}>Writing Preferences</TabsTrigger>
            <TabsTrigger value="bid-strategy" className={NAV_TRIGGER_CLASS}>Bid Strategy</TabsTrigger>
            <TabsTrigger value="export-templates" className={NAV_TRIGGER_CLASS}>Export Templates</TabsTrigger>
            <TabsTrigger value="users" className={NAV_TRIGGER_CLASS}>Users & Access</TabsTrigger>
            <TabsTrigger value="integrations" className={NAV_TRIGGER_CLASS}>Integrations</TabsTrigger>
            <TabsTrigger value="data-privacy" className={NAV_TRIGGER_CLASS}>Data &amp; Privacy</TabsTrigger>
            <div className="px-3 pt-4 pb-1 text-xs font-medium text-foreground uppercase tracking-wide">My account</div>
            <TabsTrigger value="account" className={NAV_TRIGGER_CLASS}>Account &amp; Security</TabsTrigger>
            <TabsTrigger value="notifications" className={NAV_TRIGGER_CLASS}>Notifications</TabsTrigger>
          </TabsList>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-4xl px-12 pb-4">


            <TabsContent value="users" className="space-y-6 mt-6">
              <UsersManagementTab currentUserId={user?.id ?? null} />
            </TabsContent>

            {/* Writing Preferences Tab */}
            <TabsContent value="preferences" className="space-y-6 mt-6">
              <form onSubmit={(e) => void handleSavePreferences(e)} className="space-y-6">
                {/* Perspective */}
                <section className="space-y-4">
                  <h2 className="text-sm font-medium">Perspective</h2>
                  <div className="space-y-2 max-w-md">
                    <Label htmlFor="personPerspective" className="text-meta">
                      Person Perspective
                    </Label>
                    <Select
                      {...(preferencesForm.personPerspective ? { value: preferencesForm.personPerspective } : {})}
                      onValueChange={(value) =>
                        setPreferencesForm({ ...preferencesForm, personPerspective: value as any })
                      }
                    >
                      <SelectTrigger id="personPerspective">
                        <SelectValue placeholder="Select perspective" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="first_plural">We/Our (First plural)</SelectItem>
                        <SelectItem value="third_person">Third person</SelectItem>
                        <SelectItem value="company_name">Company name</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-meta leading-relaxed">
                      Default for narrative sections (cover letters, executive summaries, methodology). Section conventions take precedence — scope of works stays impersonal, case studies stay third-person. Bid brief voice can override per bid.
                    </p>
                  </div>
                </section>

                {/* Reference Style */}
                <section className="space-y-4">
                  <h2 className="text-sm font-medium">Reference Style</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyReferenceStyle" className="text-meta">
                        Company Reference Style
                      </Label>
                      <Input
                        id="companyReferenceStyle"
                        value={preferencesForm.companyReferenceStyle || ''}
                        onChange={(e) =>
                          setPreferencesForm({
                            ...preferencesForm,
                            companyReferenceStyle: e.target.value,
                          })
                        }
                        placeholder="e.g., Skywave or Skywave Infrastructure Services"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="teamTerminology" className="text-meta">
                        Team Terminology
                      </Label>
                      <Select
                        {...(preferencesForm.teamTerminology ? { value: preferencesForm.teamTerminology } : {})}
                        onValueChange={(value) =>
                          setPreferencesForm({ ...preferencesForm, teamTerminology: value as any })
                        }
                      >
                        <SelectTrigger id="teamTerminology">
                          <SelectValue placeholder="Select terminology" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="team">Team</SelectItem>
                          <SelectItem value="personnel">Personnel</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                          <SelectItem value="workforce">Workforce</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </section>

                {/* Format */}
                <section className="space-y-4">
                  <h2 className="text-sm font-medium">Format</h2>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="useNumberedHeadings"
                      checked={preferencesForm.useNumberedHeadings || false}
                      onCheckedChange={(checked) =>
                        setPreferencesForm({
                          ...preferencesForm,
                          useNumberedHeadings: checked as boolean,
                        })
                      }
                    />
                    <Label htmlFor="useNumberedHeadings" className="text-sm cursor-pointer">
                      Use numbered headings
                    </Label>
                  </div>
                </section>

                {/* Library (Specs/win-loss-harvest-spec.md §1.2) */}
                <section className="space-y-4">
                  <h2 className="text-sm font-medium">Library</h2>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="autoHarvestEnabled"
                      checked={preferencesForm.autoHarvestEnabled ?? true}
                      onCheckedChange={(checked) =>
                        setPreferencesForm({
                          ...preferencesForm,
                          autoHarvestEnabled: checked as boolean,
                        })
                      }
                    />
                    <Label htmlFor="autoHarvestEnabled" className="text-sm cursor-pointer">
                      Offer to add submitted documents to the library as past-bid content
                    </Label>
                  </div>
                </section>

                {/* Save Button */}
                <div className="flex justify-end mt-6">
                  <Button type="submit" disabled={isSaving || !isPreferencesDirty}>
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : preferencesSaved ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Saved
                      </>
                    ) : (
                      'Save Preferences'
                    )}
                  </Button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="bid-strategy" className="mt-6">
              <BidStrategySubTabs />
            </TabsContent>

            <TabsContent value="export-templates" className="space-y-6 mt-6">
              <ExportTemplatesTab />
            </TabsContent>

            <TabsContent value="integrations" className="space-y-6 mt-6">
              <IntegrationsTab />
            </TabsContent>

            <TabsContent value="account" className="space-y-6 mt-6">
              <AccountSecurityTab />
            </TabsContent>

            <TabsContent value="notifications" className="space-y-6 mt-6">
              <NotificationPreferences />
            </TabsContent>

            <TabsContent value="data-privacy" className="space-y-6 mt-6">
              <DataPrivacyTab />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  )
}
